/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { Avatar } from "@eds-web/react/Avatar";
import { useLocale } from "@epic-mw/i18n";
import TimeAgo from "javascript-time-ago";
import { ReactNode, useMemo } from "react";

import { CosmosCard, CosmosLink, CosmosText, CosmosTextVariantEnum, EdsIcon, NoSSR } from "@components/elements";
import { dateToTimeAgoShort } from "@components/utils/dateFormatters";
import { toShortNum } from "@components/utils/numberFormatters";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

const defaultAvatarUrls = [
	"https://d3kjluh73b9h9o.cloudfront.net/original/4X/9/3/4/934e470e363de4ab12b3d046c93deddf851f0f65.png",
	"https://d3kjluh73b9h9o.cloudfront.net/original/4X/2/8/5/28502927f1d97ba1051b3b47ccfe17233bf41ef0.png",
];

export interface CommunityForumRowProps {
	name: string;
	url: string;
	post_count: number;
	topic_count: number;
	topics_weekly_count: number;
	last_poster?: {
		avatar_url: string;
		profile_url: string;
		badges?: string[];
		username: string;
	};
	last_activity_title: string;
	last_activity_at: string;
	last_activity_url: string;
	className?: string;
	subcategories?: {
		name: string;
		url: string;
	}[];
}

const styled = css({
	alignItems: "center",
	columnGap: "eds.dimension.32!",
	display: "flex",
	flexWrap: { smOnly: "wrap" },
	px: "eds.dimension.24",
	py: "eds.dimension.12",
	rowGap: "eds.dimension.12!",
	w: "100%",
	h: "eds.dimension.56",
	mdDown: {
		h: "unset",
	},
	smDown: {
		padding: "eds.dimension.20",
	},
	"&:focus-within": {
		zIndex: 1,
	},
});

const communityForumRowLink = css({
	flex: 1,
	overflow: "hidden",
	smDown: {
		"& .heading": {
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
		},
	},
});

export const CommunityForumRow = ({
	name,
	url,
	post_count,
	topic_count,
	last_poster,
	last_activity_title,
	last_activity_at,
	last_activity_url,
	className,
}: CommunityForumRowProps): ReactNode => {
	const locale = useLocale();
	const timeAgo = useMemo(() => new TimeAgo(locale), [locale]);
	return (
		<CosmosCard className={cx("community-forum-row", styled, className)} variant="material" direction="row">
			<CosmosCard.Link className={communityForumRowLink} href={url}>
				<CosmosText as="h4" variant={CosmosTextVariantEnum.headingSM}>
					{name}
				</CosmosText>
			</CosmosCard.Link>
			<HStack gap="eds.dimension.16">
				<HStack gap="eds.dimension.6">
					<EdsIcon icon="TalkBubbleDots" color="secondary" size="sm" />
					<CosmosText as="div" variant={CosmosTextVariantEnum.paragraphSM}>
						{toShortNum(topic_count)}
					</CosmosText>
				</HStack>
				<HStack gap="eds.dimension.6" display={{ smDown: "none", base: "flex" }}>
					<EdsIcon icon="ArrowTurnUpLeft" color="secondary" size="sm" />
					<CosmosText as="div" variant={CosmosTextVariantEnum.paragraphSM}>
						{toShortNum(post_count)}
					</CosmosText>
				</HStack>
			</HStack>
			{last_poster && (
				<HStack
					gap="eds.dimension.16"
					display={{ smDown: "none", sm: "flex" }}
					flexBasis={{ smOnly: "100%" }}
					overflow="hidden"
				>
					<Avatar
						as={CosmosLink}
						className={css({
							"& svg": {
								width: "eds.dimension.16 !important", // override EDS style
								height: "eds.dimension.16 !important",
							},
						})}
						href={last_poster.profile_url || ""}
						image={defaultAvatarUrls.includes(last_poster.avatar_url) ? undefined : last_poster.avatar_url}
						label={last_poster.username || ""}
						aria-label={last_poster.username || ""}
						hideLabel
						icon
						size="sm"
					/>
					<VStack
						alignItems="flex-start"
						gap="0"
						width={{ smOnly: "30rem", base: "18rem" }}
						maxW="full"
						overflow="hidden"
					>
						<CosmosText
							className={css({
								display: "flex",
								flexDirection: "row",
								w: "100%",
							})}
							as="div"
							variant={CosmosTextVariantEnum.paragraphSM}
						>
							<CosmosLink
								className={css({
									overflow: "hidden",
									whiteSpace: "nowrap",
									textOverflow: "ellipsis",
									minW: "0",
								})}
								href={last_activity_url}
							>
								{last_activity_title}
							</CosmosLink>
						</CosmosText>
						<CosmosText
							className={css({
								display: "flex",
								flexDirection: "row",
								w: "100%",
								whiteSpace: "nowrap",
							})}
							variant={CosmosTextVariantEnum.uiSM}
							color={"eds.text.secondary"}
						>
							<NoSSR>{`${dateToTimeAgoShort(last_activity_at, timeAgo)} - `}</NoSSR>
							<CosmosLink
								className={css({
									paddingStart: "eds.dimension.2",
									textDecoration: "underline",
									overflow: "hidden",
									whiteSpace: "nowrap",
									textOverflow: "ellipsis",
									minW: "0",
									color: "eds.icon.link.primary.default",
								})}
								href={last_poster.profile_url}
							>
								{last_poster.username}
							</CosmosLink>
						</CosmosText>
					</VStack>
				</HStack>
			)}
		</CosmosCard>
	);
};
